import { FC, ReactNode } from 'react';

import {
  FulfillmentCouponSlice,
  FulfillmentTypeSlice,
  SalesOnlyBucket,
  SettlementBucket,
  SummaryFulfillmentByType,
  SummarySalesByType,
} from '@shared/api/giftOrder/giftOrderDashboard.interface';
import { formatPrice } from '@shared/utils/formatPrice';

import { fmtPercent } from '../utils/dashboard.utils';

/**
 * 이 섹션이 답하는 질문: **결제된 매출이 정산까지 얼마나 왔는가.**
 *
 * 매출 구성 요약은 결제 시점(purchasedAt)에서 멈춘 그림이다. 여기서 그 뒤를 본다.
 * 계산식은 상조 정산 페이지가 쓰는 식 그대로다:
 *
 *   FeeRate 방식 — 매출금 − 원가 − 수수료 = 수익금
 *   Rebate  방식 — 리베이트 금액이 곧 수익금 (원가·수수료 없음)
 *
 * 두 카드의 1차 축은 **정산 대상이 됐는가** 하나로 같다.
 *   배송형 = 배송완료했는가   쿠폰형 = 발송했는가
 * 실지급/지급예정은 그 안의 2차 분해다.
 *
 * 정산미처리는 **양쪽 다 차액 역산**이다 — `netSales − 정산처리`, `netQuantity − 정산처리`.
 * 쿠폰을 그냥 세면 재발행분이 낙전예정을 부풀리기 때문. 서버가 주는 `unsentActual`(실측)은
 * 역산값을 검증하는 데만 쓴다: 실측이 더 많으면 초과 보유(폐기 대상), 모자라면 🔴 에러.
 *
 * 슬롯 밖 발송분은 두 박스 어디에도 안 들어가고 카드 하단에 따로 적는다:
 *   overSent  매출 없이 도매가만 나갔다 → 상조 낙전 수익에서 **상계(−)**
 *   extraPaid 자체 결제라 지급 대상이지만 결제액이 순매출 밖(`extraPayments[]`)에 있다
 */

/** 값이 없을 땐 숫자 대신 —. 0원과 "모름"을 구분한다. */
const won = (v: number | null | undefined) => (v == null ? '—' : formatPrice(v));

/**
 * 카드 전체가 **수량(개)** 한 단위로만 센다.
 *
 * 배송 문서 수(`bucket.count`)를 쓰면 순매출과 단위가 어긋난다 — 배송 한 건이 수량 3을
 * 담을 수 있어 `순매출 143개` 아래 `정산처리 5건`이 뜨고, 143 = 5 + ? 가 성립하지 않는다.
 * 쿠폰은 1장 = 1개라 어느 쪽이든 같지만, 두 카드를 같은 단위로 읽히게 둔다.
 */
const ea = (v: number | null | undefined) =>
  v == null ? '—' : `${v.toLocaleString()}개`;

// 수수료는 서버가 건별 올림(ceilFeeWon)으로 합산한 정수를 내려준다 — 로컬 반올림 금지
// (내림하면 정산 페이지·실지급액과 1원 어긋난다). null만 그대로 통과시킨다.

type SettlementMethod = FulfillmentTypeSlice['method'];

const METHOD_LABEL: Record<SettlementMethod, string> = {
  FeeRate: '수수료 방식',
  Rebate: '리베이트 방식',
  Mixed: '혼합 방식',
};

/** Rebate 방식만 원가·수수료 칸을 접는다. Mixed는 FeeRate 상조분이 섞여 있어 펼친다. */
const isRebate = (method: SettlementMethod) => method === 'Rebate';

/** Rebate 방식이면 cogs/fee가 양쪽 다 null이라 합쳐도 null로 남는다. */
const sumBuckets = (a: SettlementBucket, b: SettlementBucket): SettlementBucket => ({
  count: a.count + b.count,
  quantity: a.quantity + b.quantity,
  sales: a.sales + b.sales,
  cogs: a.cogs == null && b.cogs == null ? null : (a.cogs ?? 0) + (b.cogs ?? 0),
  fee: a.fee == null && b.fee == null ? null : (a.fee ?? 0) + (b.fee ?? 0),
  profit: a.profit + b.profit,
});

const sumSalesOnly = (...xs: SalesOnlyBucket[]): SalesOnlyBucket =>
  xs.reduce((t, x) => ({ count: t?.count + x?.count, sales: t?.sales + x?.sales }), {
    count: 0,
    sales: 0,
  });

/* ── 공용 조각 ── */

const Line: FC<{
  label: string;
  value: number | null;
  muted?: boolean;
  negative?: boolean;
}> = ({ label, value, muted, negative }) => (
  <div className="flex items-baseline justify-between gap-2">
    <dt className={muted ? 'text-gray-500' : 'text-gray-600'}>{label}</dt>
    <dd
      className={`tabular-nums ${muted ? 'text-gray-600' : 'font-medium text-gray-900'}`}
    >
      {negative && value ? '-' : ''}
      {won(value)}
    </dd>
  </div>
);

/** 정산처리 — 인디고로 채운다. 여기가 카드의 결론이다. */
const ProcessedBox: FC<{
  bucket: SettlementBucket | null;
  rebate: boolean;
  /** 배송형은 '원가', 쿠폰형은 '도매가' */
  cogsLabel: string;
  hint: string;
  children: ReactNode;
}> = ({ bucket, rebate, cogsLabel, hint, children }) => (
  <div className="flex flex-col rounded-lg border border-indigo-200 bg-indigo-50/60 px-3.5 py-3">
    <div className="flex items-baseline justify-between gap-2">
      <p className="flex items-center gap-1.5 text-xs font-semibold text-indigo-800">
        <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-indigo-500" />
        정산처리
        <span className="font-normal text-gray-400">{hint}</span>
      </p>
      {bucket && (
        <p className="shrink-0 text-[11px] tabular-nums text-gray-400">
          {ea(bucket.quantity)}
        </p>
      )}
    </div>

    <dl className="mt-2 space-y-1 text-sm">
      <Line label="매출금" value={bucket?.sales ?? null} />
      {/* Rebate 방식 상조는 원가·수수료 개념이 없다 — 리베이트 금액이 곧 수익금 */}
      {!rebate && (
        <>
          <Line label={cogsLabel} value={bucket?.cogs ?? null} muted negative />
          <Line label="수수료" value={bucket?.fee ?? null} muted negative />
        </>
      )}
      <div className="flex items-baseline justify-between border-t border-indigo-200/70 pt-1.5">
        <dt className="font-semibold text-indigo-700">수익금</dt>
        <dd className="text-lg font-bold tabular-nums text-indigo-700">
          {won(bucket?.profit ?? null)}
        </dd>
      </div>
    </dl>

    {children}
  </div>
);

/** 정산처리 안의 2차 분해 한 줄. 돈이 나갔는지가 관심사라 수익금만 보여준다. */
const SubRow: FC<{
  label: string;
  quantity?: number;
  value: number | null;
  paid: boolean;
}> = ({ label, quantity, value, paid }) => (
  <div className="flex items-baseline justify-between gap-2 text-xs">
    <dt className="flex items-center gap-1.5 text-gray-500">
      <span
        className={`h-1 w-1 shrink-0 rounded-full ${
          paid ? 'bg-indigo-400' : 'border border-indigo-300'
        }`}
      />
      {label}
      {quantity != null && (
        <span className="tabular-nums text-gray-400">{ea(quantity)}</span>
      )}
    </dt>
    <dd className="font-medium tabular-nums text-gray-700">{won(value)}</dd>
  </div>
);

/**
 * 정산미처리 — 회색. 매출금 하나뿐이다.
 * 원가·수수료·수익금은 배송이 끝나거나 쿠폰이 발송되기 전엔 정해지지 않는다.
 */
const UnprocessedBox: FC<{
  sales: number | null;
  quantity?: number | null;
  hint: string;
  note: string;
  children?: ReactNode;
}> = ({ sales, quantity, hint, note, children }) => (
  <div className="flex flex-col rounded-lg border border-gray-200 bg-gray-50/80 px-3.5 py-3">
    <div className="flex items-baseline justify-between gap-2">
      <p className="flex items-center gap-1.5 text-xs font-semibold text-gray-600">
        <span className="h-1.5 w-1.5 shrink-0 rounded-full border border-gray-400" />
        정산미처리
        <span className="font-normal text-gray-400">{hint}</span>
      </p>
      {quantity != null && (
        <p className="shrink-0 text-[11px] tabular-nums text-gray-400">{ea(quantity)}</p>
      )}
    </div>

    <dl className="mt-2 flex items-baseline justify-between gap-2 text-sm">
      <dt className="text-gray-600">매출금</dt>
      <dd className="text-lg font-bold tabular-nums text-gray-700">{won(sales)}</dd>
    </dl>

    {children}

    <p className="mt-2 border-t border-gray-200 pt-1.5 text-[11px] leading-relaxed text-gray-400">
      {note}
    </p>
  </div>
);

/** 정합성이 깨졌을 때만 뜬다. 조용히 틀린 숫자를 보여주느니 대놓고 깨진 걸 알린다. */
const ErrorNote: FC<{ children: ReactNode }> = ({ children }) => (
  <p className="mt-2 rounded border border-red-200 bg-red-50 px-2 py-1.5 text-[11px] font-medium leading-relaxed text-red-700">
    🔴 {children}
  </p>
);

/** 미발송 하위 분해 한 줄 (나중에 받기 / 낙전 후보). 쿠폰은 1장 = 1개다. */
const UnsentRow: FC<{ label: string; bucket: SalesOnlyBucket | null }> = ({
  label,
  bucket,
}) => (
  <div className="flex items-baseline justify-between gap-2 text-xs">
    <dt className="flex items-center gap-1.5 text-gray-500">
      <span className="h-1 w-1 shrink-0 rounded-full border border-gray-400" />
      {label}
      {bucket && <span className="tabular-nums text-gray-400">{ea(bucket.count)}</span>}
    </dt>
    <dd className="font-medium tabular-nums text-gray-700">
      {won(bucket?.sales ?? null)}
    </dd>
  </div>
);

/**
 * 낙전 블록의 한 줄. 값이 `null`이면 —(미상)으로 둔다.
 * 아직 없는 트랙(낙전환불)도 칸을 비워 두고 미리 보여준다 — 생기면 숫자만 채워진다.
 */
const WindfallRow: FC<{
  label: string;
  hint?: string;
  quantity?: number | null;
  value: number | null;
  negative?: boolean;
  muted?: boolean;
}> = ({ label, hint, quantity, value, negative, muted }) => (
  <div className="flex items-baseline justify-between gap-2 text-xs">
    <dt className={muted ? 'text-amber-700/70' : 'text-amber-800'}>
      {label}
      {hint && <span className="ml-1 text-[11px] text-amber-700/50">{hint}</span>}
      {quantity != null && (
        <span className="ml-1.5 tabular-nums text-amber-700/60">{ea(quantity)}</span>
      )}
    </dt>
    <dd
      className={`shrink-0 tabular-nums ${
        negative ? 'font-medium text-red-600' : 'font-medium text-amber-900'
      }`}
    >
      {negative && value ? '-' : ''}
      {won(value)}
    </dd>
  </div>
);

const CardShell: FC<{
  title: string;
  method?: SettlementMethod;
  rate: number | null;
  netSales: number | null;
  /** 순매출에 대응하는 순수량. 아래 버킷의 `건`(문서 수)과는 다른 단위다 */
  netQuantity: number | null;
  children: ReactNode;
  footer?: ReactNode;
}> = ({ title, method, rate, netSales, netQuantity, children, footer }) => (
  <div className="rounded-lg border border-gray-200 bg-white p-4">
    <div className="flex items-center justify-between gap-2">
      <div className="flex items-center gap-2">
        <h3 className="text-base font-bold text-gray-800">{title}</h3>
        {method ? (
          <span className="rounded bg-gray-100 px-1.5 py-0.5 text-[11px] font-medium text-gray-500">
            {METHOD_LABEL[method]}
          </span>
        ) : (
          <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[11px] font-medium text-amber-700">
            데이터 준비 중
          </span>
        )}
      </div>
      <p className="shrink-0 text-xs text-gray-400">
        정산처리율{' '}
        <span className="font-medium tabular-nums text-gray-600">
          {rate == null ? '—' : fmtPercent(rate)}
        </span>
      </p>
    </div>

    <dl className="mt-3 flex items-baseline justify-between gap-2 border-b border-gray-100 pb-2.5">
      <dt className="text-sm text-gray-500">
        순매출
        {netQuantity != null && (
          <span className="ml-1.5 text-xs tabular-nums text-gray-400">
            {netQuantity.toLocaleString()}개
          </span>
        )}
      </dt>
      <dd className="text-xl font-bold tabular-nums text-gray-900">{won(netSales)}</dd>
    </dl>

    <div className="mt-3 grid gap-2.5 sm:grid-cols-2">{children}</div>
    {footer}
  </div>
);

/* ── 배송형 ── */

const DeliveryCard: FC<{
  slice: FulfillmentTypeSlice | null;
  netSales: number | null;
  netQuantity: number | null;
}> = ({ slice, netSales, netQuantity }) => {
  const rebate = slice ? isRebate(slice.method) : false;
  const processed = slice ? sumBuckets(slice.settled, slice.pending) : null;

  // 서버는 정산 대상이 된 배송(배송완료)만 센다. 나머지는 순매출과의 차액으로 구한다.
  // 금액·수량 모두 같은 방식이라 `순매출 = 정산처리 + 정산미처리`가 양쪽에서 성립한다.
  // 취소분은 netSales/netQuantity에서 이미 빠져 있어 음수가 날 수 있으므로 0으로 막는다.
  const unprocessedSales =
    processed && netSales != null ? Math.max(0, netSales - processed.sales) : null;
  const unprocessedQuantity =
    processed && netQuantity != null
      ? Math.max(0, netQuantity - processed.quantity)
      : null;
  const rate = processed && netSales && netSales > 0 ? processed.sales / netSales : null;

  return (
    <CardShell
      title="배송형"
      method={slice?.method}
      rate={rate}
      netSales={netSales}
      netQuantity={netQuantity}
    >
      <ProcessedBox bucket={processed} rebate={rebate} cogsLabel="원가" hint="배송완료">
        <dl className="mt-2.5 space-y-1 border-t border-indigo-200/70 pt-2">
          <SubRow
            label="실지급"
            quantity={slice?.settled.quantity}
            value={slice?.settled.profit ?? null}
            paid
          />
          <SubRow
            label="지급예정"
            quantity={slice?.pending.quantity}
            value={slice?.pending.profit ?? null}
            paid={false}
          />
        </dl>
      </ProcessedBox>

      {/* 주소 입력 전·준비중·배송중이 모두 여기 섞인다 — "배송 중"이라 쓰면 오해를 부른다 */}
      <UnprocessedBox
        sales={unprocessedSales}
        quantity={unprocessedQuantity}
        hint="배송완료 전"
        note="주소 입력 전·준비중·배송중이 모두 여기 포함됩니다. 배송이 완료되어야 정산 대상이 되고, 원가·수수료·수익금은 그때 확정됩니다."
      />
    </CardShell>
  );
};

/* ── 쿠폰형 ── */

/**
 * 카드 하단 보조 줄 (환불 차감 · 슬롯 밖 발행분). 0건이면 그리지 않는다.
 *
 * 줄마다 값의 종류가 다르다 — 환불은 차감액, 추가결제는 수익금, 나머지는 매출금.
 * 그래서 `valueLabel`로 무슨 돈인지 값 앞에 박아둔다.
 */
const FooterRow: FC<{
  label: string;
  hint: string;
  quantity: number;
  value: number;
  valueLabel?: string;
  negative?: boolean;
}> = ({ label, hint, quantity, value, valueLabel, negative }) => (
  <div className="flex items-baseline justify-between gap-2 text-xs">
    <dt className="text-gray-500">
      {label}
      <span className="ml-1 text-[11px] text-gray-400">{hint}</span>
      <span className="ml-1.5 tabular-nums text-gray-400">{ea(quantity)}</span>
    </dt>
    <dd className="shrink-0">
      {valueLabel && <span className="mr-1 text-[11px] text-gray-400">{valueLabel}</span>}
      <span
        className={`font-semibold tabular-nums ${
          negative ? 'text-red-600' : 'text-gray-700'
        }`}
      >
        {negative && value ? '-' : ''}
        {won(value)}
      </span>
    </dd>
  </div>
);

const CouponCard: FC<{
  slice: FulfillmentCouponSlice | null;
  netSales: number | null;
  netQuantity: number | null;
}> = ({ slice, netSales, netQuantity }) => {
  const rebate = slice ? isRebate(slice.method) : false;
  const processed = slice ? sumBuckets(slice.settled, slice.pending) : null;

  // 미발송은 쿠폰 문서를 세지 않고 차액으로 구한다 — 그냥 세면 재발행분이 낙전예정을 부풀린다.
  // 슬롯 축이라 `순매출 = 정산처리 + 정산미처리`가 수량·금액 양쪽에서 닫힌다.
  const unsentSlots =
    processed && netSales != null && netQuantity != null
      ? { quantity: netQuantity - processed.quantity, sales: netSales - processed.sales }
      : null;

  // 실측은 검증용이다. 슬롯보다 많으면 초과 보유(폐기 대상), 적으면 팔았는데 줄 쿠폰이 없다.
  const actual = slice
    ? sumSalesOnly(
        slice.unsentActual.later,
        slice.unsentActual.windfall,
        slice.unsentActual.refunded,
      )
    : null;
  const diff = actual && unsentSlots ? actual.count - unsentSlots.quantity : null;

  const slotsNegative = !!unsentSlots && unsentSlots.quantity < 0;
  const couponsMissing = diff != null && diff < 0;

  const rate = processed && netSales && netSales > 0 ? processed.sales / netSales : null;

  // ── 낙전 예상 ──────────────────────────────────────────────────────────────
  // 낙전 base는 미발송 **슬롯**(역산)에서 "나중에 받기"(실측)를 뺀 것이다.
  // 나중에 받기는 수신번호 인증까지 마쳐 발송이 유력하므로 낙전 후보가 아니다.
  const later = slice?.unsentActual.later ?? null;
  const windfallSales =
    unsentSlots && later ? Math.max(0, unsentSlots.sales - later.sales) : null;
  const windfallQuantity =
    unsentSlots && later ? Math.max(0, unsentSlots.quantity - later.count) : null;

  // 실효 수수료율 — 상조마다 요율이 다르고 Rebate는 0이라 상수로 못 쓴다. 집계값에서 역산한다.
  const effectiveFeeRate =
    processed && processed.fee != null && processed.sales > 0
      ? processed.fee / processed.sales
      : null;
  // 예상치라 정확할 수 없지만 반올림 방향은 정산 규칙(수수료 건별 올림)과 맞춘다.
  const windfallFee =
    windfallSales != null && effectiveFeeRate != null
      ? Math.ceil(windfallSales * effectiveFeeRate - 1e-6)
      : null;

  // 초과발송은 매출 없이 도매가만 나갔다 → 상조 낙전 수익에서 상계한다.
  // 서버가 아직 salePriceRetail을 매출로 잡아 sales/profit을 양수로 내므로 그 둘은 쓰지 않고,
  // cogs(= Σ gifticon.priceWhole)만 읽는다. Rebate 상조는 cogs가 null → 상계 대상 없음.
  const overSentOffset = slice?.overSent.cogs ?? null;

  // 상조 낙전 수익(예상) = 낙전 매출 − 수수료 − 초과발송 상계 − 낙전환불 차감(미구현)
  const windfallProfit =
    !rebate && windfallSales != null && windfallFee != null
      ? windfallSales - windfallFee - (overSentOffset ?? 0)
      : null;
  // 낙전 예상율 = 낙전 매출 ÷ 순매출. 판 돈 중 얼마가 낙전으로 굳을지.
  const windfallRate =
    windfallSales != null && netSales && netSales > 0 ? windfallSales / netSales : null;

  const windfallBlock = (
    <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50/70 px-3.5 py-3">
      <div className="flex items-baseline justify-between gap-2">
        <p className="flex items-center gap-1.5 text-xs font-semibold text-amber-800">
          <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-amber-500" />
          낙전 예상
          <span className="font-normal text-amber-700/50">유효기간 만료 시 확정</span>
        </p>
        <p className="shrink-0 text-[11px] text-amber-700/70">
          낙전 예상율{' '}
          <span className="font-semibold tabular-nums text-amber-900">
            {windfallRate == null ? '—' : fmtPercent(windfallRate)}
          </span>
        </p>
      </div>

      <dl className="mt-2 space-y-1">
        <WindfallRow
          label="낙전 매출"
          hint="미발송 − 나중에 받기"
          quantity={windfallQuantity}
          value={windfallSales}
        />
        {!rebate && (
          <WindfallRow
            label="수수료"
            hint={
              effectiveFeeRate == null ? undefined : `판매가 × ${fmtPercent(effectiveFeeRate)}`
            }
            value={windfallFee}
            negative
            muted
          />
        )}
        {!!slice?.overSent.quantity && (
          <WindfallRow
            label="초과발송 상계"
            hint="초과 발송 도매가"
            quantity={slice.overSent.quantity}
            value={overSentOffset}
            negative={overSentOffset != null}
            muted
          />
        )}
        {/* 낙전환불(couponWindfallRefundSettlement) — 아직 트랙이 없다. 칸만 잡아 둔다. */}
        <WindfallRow
          label="낙전환불 차감"
          hint="미발송 90% 환불 · 트랙 준비 중"
          quantity={slice?.unsentActual.refunded.count ?? null}
          value={null}
          muted
        />

        <div className="flex items-baseline justify-between gap-2 border-t border-amber-200/70 pt-1.5">
          <dt className="text-sm font-semibold text-amber-800">현재 기대 낙전 수익</dt>
          <dd className="text-lg font-bold tabular-nums text-amber-900">
            {rebate ? '—' : won(windfallProfit)}
          </dd>
        </div>
      </dl>

      {rebate && (
        <p className="mt-1.5 text-[11px] leading-relaxed text-amber-700/70">
          리베이트 방식은 낙전 수익 대상이 아닙니다. 초과발송 도매가도 상계하지 않습니다.
        </p>
      )}
    </div>
  );

  const footer = (
    <>
      {windfallBlock}
      {slice && (slice.refund.count > 0 || slice.extraPaid.quantity > 0) && (
        <dl className="mt-3 space-y-1 border-t border-gray-100 pt-2.5">
          {slice.refund.count > 0 && (
            <FooterRow
              label="추가 발생한 환불금"
              hint="발송 후 환불·연장"
              quantity={slice.refund.count}
              value={slice.refund.amount}
              negative
            />
          )}
          {/* 슬롯 밖이지만 자체 결제를 받았으므로 상조에 지급된다 — 위 수익금엔 없는 돈이다 */}
          {slice.extraPaid.quantity > 0 && (
            <FooterRow
              label="추가결제 발송"
              hint="슬롯 밖 · 지급 대상 (순매출 밖)"
              quantity={slice.extraPaid.quantity}
              value={slice.extraPaid.profit}
              valueLabel="수익금"
            />
          )}
        </dl>
      )}
    </>
  );

  return (
    <CardShell
      title="쿠폰형"
      method={slice?.method}
      rate={rate}
      netSales={netSales}
      netQuantity={netQuantity}
      footer={footer}
    >
      <ProcessedBox bucket={processed} rebate={rebate} cogsLabel="도매가" hint="발송완료">
        <dl className="mt-2.5 space-y-1 border-t border-indigo-200/70 pt-2">
          <SubRow
            label="실지급"
            quantity={slice?.settled.quantity}
            value={slice?.settled.profit ?? null}
            paid
          />
          <SubRow
            label="지급예정"
            quantity={slice?.pending.quantity}
            value={slice?.pending.profit ?? null}
            paid={false}
          />
        </dl>
      </ProcessedBox>

      <UnprocessedBox
        sales={unsentSlots?.sales ?? null}
        quantity={unsentSlots?.quantity ?? null}
        hint="미발송"
        note="발송 전이라 도매가·수수료·수익금이 정해지지 않습니다. 유효기간이 지나면 낙전으로 확정됩니다."
      >
        {slotsNegative && (
          <ErrorNote>
            발송 인정 수량이 순매출 수량을 넘었습니다. 쿼터 가드가 적용되지 않았을 수 있습니다.
          </ErrorNote>
        )}

        {/* 아래는 실제 쿠폰 문서 실측치다. 위 매출금(역산)과 단위는 같아도 출처가 다르다. */}
        <dl className="mt-2 space-y-1 border-t border-gray-200 pt-2">
          <UnsentRow label="나중에 받기" bucket={slice?.unsentActual.later ?? null} />
          <UnsentRow label="낙전 후보" bucket={slice?.unsentActual.windfall ?? null} />
          {/* 미발송 90% 환불 — 실건수 0. 생기면 자동으로 나타난다 */}
          {!!slice?.unsentActual.refunded.count && (
            <UnsentRow label="미발송 환불" bucket={slice.unsentActual.refunded} />
          )}
        </dl>

        {couponsMissing ? (
          <ErrorNote>
            남은 쿠폰이 {ea(-(diff as number))} 모자랍니다. 팔았는데 줄 쿠폰이 없습니다.
          </ErrorNote>
        ) : (
          !!diff && (
            <p className="mt-1.5 text-[11px] leading-relaxed text-gray-400">
              쿠폰이 {ea(diff)} 더 남아 있습니다 (재발행 등). 낙전 확정 시 폐기 대상입니다.
            </p>
          )
        )}
      </UnprocessedBox>
    </CardShell>
  );
};

export const DashboardFulfillmentSection: FC<{
  fulfillment?: SummaryFulfillmentByType;
  salesByType?: SummarySalesByType;
}> = ({ fulfillment, salesByType }) => (
  <section className="rounded-lg bg-white p-6 shadow">
    <div className="mb-4">
      <h2 className="flex items-center gap-2 text-lg font-bold text-gray-800">
        이행 현황
        <span className="text-xs font-normal text-gray-400">
          위 매출의 정산처리 / 정산미처리 구분
        </span>
      </h2>
      <p className="mt-1 text-xs text-gray-400">
        정산처리는 배송완료·발송되어 정산 대상이 된 매출이고, 그 안에서 실지급과
        지급예정으로 나뉩니다. 정산미처리는 아직 정산 대상이 아닌 매출로, 그대로 잠재
        낙전이자 잠재 원가입니다.
      </p>
    </div>

    <div className="grid gap-4 lg:grid-cols-2">
      <CouponCard
        slice={fulfillment?.coupon ?? null}
        netSales={salesByType?.coupon.netSales ?? null}
        netQuantity={salesByType?.coupon.netQuantity ?? null}
      />
      <DeliveryCard
        slice={fulfillment?.delivery ?? null}
        netSales={salesByType?.delivery.netSales ?? null}
        netQuantity={salesByType?.delivery.netQuantity ?? null}
      />
    </div>

    <p className="mt-2 text-right text-xs text-gray-400">단위: 원</p>
  </section>
);
